import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { SiteHeader, SiteFooter } from '../components/SiteLayout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Storefront, ShieldCheck, Lock, Database, Code, FileText, Package,
  CaretRight, CheckCircle, Lightning, ArrowRight, Sparkle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import TI from '../components/brand/TI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =============================================================
 *  ToxIntel Marketplace — public catalog + brand purchase flow
 *  Aesthetic: dark navy + coral accents, Bloomberg-terminal vibe
 * ===========================================================*/

const FAMILY_ICON = {
  ingredient_db: Database,
  packaging_rsl: Package,
  ingredient_report: FileText,
  packaging_report: Package,
  enterprise_api: Code,
};

const FAMILY_COLOR = {
  ingredient_db: '#FF4E6A',
  packaging_rsl: '#9F7AEA',
  ingredient_report: '#0EA5E9',
  packaging_report: '#10B981',
  enterprise_api: '#F59E0B',
};

function priceLabel(tier) {
  if (tier.price_label) return tier.price_label;
  if (tier.price_monthly_usd) {
    if (tier.price_annual_usd) {
      return `$${tier.price_monthly_usd.toLocaleString()}/mo · $${tier.price_annual_usd.toLocaleString()}/yr`;
    }
    return `$${tier.price_monthly_usd.toLocaleString()}/mo`;
  }
  if (tier.price_monthly_usd_min && tier.price_monthly_usd_max) {
    return `$${tier.price_monthly_usd_min.toLocaleString()}–$${tier.price_monthly_usd_max.toLocaleString()}/mo`;
  }
  if (tier.default_price_usd) {
    return `from $${tier.price_min_usd}/report · typical $${tier.default_price_usd}`;
  }
  return 'Quote on request';
}

function PillarStrip() {
  const pillars = [
    { icon: ShieldCheck, label: 'Tokenised access' },
    { icon: Lock, label: 'No raw DB exports' },
    { icon: Database, label: 'Audit-logged' },
    { icon: Code, label: 'Signed-URL delivery' },
  ];
  return (
    <div className="flex flex-wrap gap-3 mt-6">
      {pillars.map((p) => (
        <div key={p.label} className="flex items-center gap-2 px-3 py-2 rounded border border-white/10 bg-white/5 backdrop-blur">
          <p.icon size={14} weight="duotone" className="text-[#FF4E6A]" />
          <span className="text-xs font-medium text-white/80">{p.label}</span>
        </div>
      ))}
    </div>
  );
}

function TierCard({ family, tier, isAuthenticated, onSubscribe, onPurchase, onQuote }) {
  const isReport = ['ingredient_basic', 'ingredient_professional', 'ingredient_scientific',
                    'packaging_basic', 'packaging_advanced'].includes(tier.code);
  const isQuote = tier.requires_quote;
  const color = FAMILY_COLOR[family.key] || '#FF4E6A';

  const ctaLabel = isQuote ? 'Request quote' : (isReport ? 'Purchase report' : 'Request license');
  const onClick = () => {
    if (!isAuthenticated) {
      // Public visitors → Portfolio request portal for enterprise/quote items; login otherwise
      if (isQuote) { window.location.href = '/portfolio'; } else { window.location.href = '/login?next=/marketplace'; }
      return;
    }
    if (isQuote) onQuote(family, tier);
    else if (isReport) onPurchase(family, tier);
    else onSubscribe(family, tier);
  };

  return (
    <div
      className="card-flat bg-white border border-gray-200 hover:border-[#0B1426] transition-colors p-5 flex flex-col"
      data-testid={`mp-tier-${tier.code}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563]" style={{ color }}>{family.name}</p>
          <h3 className="text-base font-bold text-[#111827] mt-0.5">{tier.name}</h3>
        </div>
        {tier.admin_review_required && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">Aᴳ-reviewed</span>}
      </div>
      <p className="text-lg font-black tabular-nums text-[#0B1426] mt-2">{priceLabel(tier)}</p>
      {tier.delivery && <p className="text-[11px] text-[#4B5563] mt-1">{tier.delivery}</p>}
      {tier.lookup_quota_per_month && <p className="text-[11px] text-[#4B5563] mt-1">Up to {tier.lookup_quota_per_month.toLocaleString()} lookups / month</p>}
      <ul className="space-y-1 mt-3 mb-4 flex-1">
        {(tier.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-1.5 text-[12px] text-[#374151]">
            <CheckCircle size={11} weight="fill" className="text-[#10B981] mt-0.5 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {tier.limits && <p className="text-[10px] text-[#9CA3AF] italic mb-2">{tier.limits}</p>}
      <Button
        onClick={onClick}
        className="h-9 rounded text-white text-sm font-semibold w-full mt-auto"
        style={{ backgroundColor: '#0B1426' }}
        data-testid={`mp-cta-${tier.code}`}
      >
        {ctaLabel} <ArrowRight size={13} weight="bold" className="ml-1.5" />
      </Button>
    </div>
  );
}

function SubscribeDialog({ open, onOpenChange, family, tier, onSubmitted }) {
  const [billing, setBilling] = useState('monthly');
  const [useCase, setUseCase] = useState('');
  const [company, setCompany] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) { setBilling('monthly'); setUseCase(''); setCompany(''); setErr(''); }
  }, [open]);

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      await axios.post(`${API_URL}/api/marketplace/subscriptions/request`,
        { plan_code: tier.code, billing_period: billing, use_case: useCase, company },
        { withCredentials: true });
      toast.success('Quote request submitted — Aᴳ admin will review.');
      onSubmitted();
      onOpenChange(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to submit request');
    } finally { setSaving(false); }
  };

  const payNow = async () => {
    setSaving(true); setErr('');
    try {
      const r = await axios.post(`${API_URL}/api/marketplace/stripe/checkout/subscription`,
        { plan_code: tier.code, billing_period: billing, company, use_case: useCase,
          origin_url: window.location.origin },
        { withCredentials: true });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        setErr('Stripe session could not be created');
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Stripe checkout failed');
    } finally { setSaving(false); }
  };

  if (!tier) return null;
  const annualPrice = tier.price_annual_usd;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mp-subscribe-dialog">
        <DialogHeader>
          <DialogTitle>Request license — {tier.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#4B5563]">
            Your request is submitted to the Aᴳ Marketplace admin. We will issue an enterprise invoice
            and activate access on receipt. Payment is not collected on this form.
          </p>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Billing period</Label>
            <div className="flex gap-2 mt-1">
              {['monthly', annualPrice ? 'annual' : null].filter(Boolean).map(p => (
                <button key={p} onClick={() => setBilling(p)} type="button"
                  className={`flex-1 px-3 py-2 rounded border text-sm font-semibold ${billing === p ? 'border-[#0B1426] bg-[#0B1426] text-white' : 'border-gray-300 bg-white text-[#374151]'}`}
                  data-testid={`mp-billing-${p}`}>
                  {p === 'monthly' ? `$${tier.price_monthly_usd?.toLocaleString()}/mo` : `$${annualPrice?.toLocaleString()}/yr`}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Company</Label>
            <Input value={company} onChange={(e) => setCompany(e.target.value)} className="mt-1 h-9 text-sm" data-testid="mp-company" />
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Use case (optional)</Label>
            <Textarea rows={3} value={useCase} onChange={(e) => setUseCase(e.target.value)} className="text-sm" placeholder="Number of skus / users / integrations…" data-testid="mp-use-case" />
          </div>
          {err && <p className="text-[12px] text-[#9B1C1C]" data-testid="mp-subscribe-error">{err}</p>}
          <div className="flex gap-2">
            <Button onClick={payNow} disabled={saving} className="flex-1 h-10 rounded bg-[#635BFF] hover:bg-[#4E47CC] text-white font-semibold" data-testid="mp-pay-stripe">
              {saving ? 'Redirecting…' : 'Pay with Stripe →'}
            </Button>
            <Button onClick={submit} disabled={saving} variant="outline" className="flex-1 h-10 rounded font-semibold" data-testid="mp-subscribe-submit">
              {saving ? '…' : 'Request quote (no card)'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PurchaseDialog({ open, onOpenChange, family, tier, onSubmitted }) {
  const [itemRef, setItemRef] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const itemType = family?.key === 'packaging_report' ? 'packaging' : 'ingredient';

  useEffect(() => { if (open) { setItemRef(''); setNotes(''); setErr(''); } }, [open]);

  const submit = async () => {
    if (!itemRef.trim()) { setErr('Enter an INCI / material identifier'); return; }
    setSaving(true); setErr('');
    try {
      const r = await axios.post(`${API_URL}/api/marketplace/purchases`,
        { tier_code: tier.code, item_type: itemType, item_ref: itemRef.trim(), notes },
        { withCredentials: true });
      if (r.data?.status === 'delivered') toast.success('Report generated — view it on My Licenses.');
      else if (r.data?.status === 'pending_review') toast.success('Purchase received — Aᴳ assessor will review and deliver.');
      else toast.success('Purchase received.');
      onSubmitted();
      onOpenChange(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to submit purchase');
    } finally { setSaving(false); }
  };

  const payNow = async () => {
    if (!itemRef.trim()) { setErr('Enter an INCI / material identifier'); return; }
    setSaving(true); setErr('');
    try {
      const r = await axios.post(`${API_URL}/api/marketplace/stripe/checkout/purchase`,
        { tier_code: tier.code, item_type: itemType, item_ref: itemRef.trim(), notes,
          origin_url: window.location.origin },
        { withCredentials: true });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        setErr('Stripe session could not be created');
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Stripe checkout failed');
    } finally { setSaving(false); }
  };

  if (!tier) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mp-purchase-dialog">
        <DialogHeader>
          <DialogTitle>Purchase report — {tier.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#4B5563]">
            On submission, the report is {tier.admin_review_required ? <strong>queued for Aᴳ assessor review</strong> : <strong>auto-generated</strong>}.
            It will appear in <Link to="/marketplace/my-licenses" className="text-[#FF4E6A] font-semibold">My Licenses</Link>.
          </p>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">
              {itemType === 'ingredient' ? 'Ingredient INCI' : 'Material identifier'}
            </Label>
            <Input value={itemRef} onChange={(e) => setItemRef(e.target.value)} className="mt-1 h-9 text-sm"
              placeholder={itemType === 'ingredient' ? 'Glycerin' : 'PET bottle with PCR resin'}
              data-testid="mp-item-ref" />
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm" data-testid="mp-notes" />
          </div>
          {err && <p className="text-[12px] text-[#9B1C1C]" data-testid="mp-purchase-error">{err}</p>}
          <div className="flex gap-2">
            <Button onClick={payNow} disabled={saving} className="flex-1 h-10 rounded bg-[#635BFF] hover:bg-[#4E47CC] text-white font-semibold" data-testid="mp-pay-stripe-purchase">
              {saving ? 'Redirecting…' : `Pay $${tier.default_price_usd} →`}
            </Button>
            <Button onClick={submit} disabled={saving} variant="outline" className="flex-1 h-10 rounded font-semibold" data-testid="mp-purchase-submit">
              {saving ? '…' : 'Request manual invoice'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MarketplacePage() {
  const { isAuthenticated, isAdmin } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [activeTab, setActiveTab] = useState('ingredient_db');
  const [subTier, setSubTier] = useState(null);
  const [subFamily, setSubFamily] = useState(null);
  const [subOpen, setSubOpen] = useState(false);
  const [purTier, setPurTier] = useState(null);
  const [purFamily, setPurFamily] = useState(null);
  const [purOpen, setPurOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/marketplace/catalog`);
        setCatalog(r.data);
      } catch (e) { /* noop */ }
    })();
  }, []);

  const onSubscribe = (family, tier) => { setSubFamily(family); setSubTier(tier); setSubOpen(true); };
  const onPurchase = (family, tier) => { setPurFamily(family); setPurTier(tier); setPurOpen(true); };
  const onQuote = (_family, _tier) => { window.location.href = '/portfolio'; };

  const activeFamily = catalog?.families?.find(f => f.key === activeTab);
  const ActiveIcon = activeFamily ? FAMILY_ICON[activeFamily.key] : Storefront;

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <SiteHeader />

      {/* Hero — dark navy */}
      <section className="relative bg-[#0B1426] text-white" data-testid="mp-hero">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative max-w-7xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 mb-3">
            <Storefront size={18} weight="duotone" className="text-[#FF4E6A]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60"><TI color="#FF4E6A" /> · Marketplace</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight max-w-3xl">
            Scientific infrastructure for retailer readiness, regulatory intelligence, and defensible product stewardship.
          </h1>
          <p className="text-base text-white/70 max-w-2xl mt-4">
            Licensed access to the Aᴳ Ingredient Intelligence Database, Packaging RSL Database, and per-item scientific reports —
            tokenised, audit-logged, and architecturally protected. No raw database exposure. No methodology downloads.
          </p>
          <div className="flex gap-3 mt-6">
            {isAuthenticated && (
              <Link to="/marketplace/my-licenses">
                <Button className="h-10 bg-[#FF4E6A] hover:bg-[#E04057] text-white text-sm font-semibold px-5 rounded" data-testid="mp-to-my-licenses">
                  My Licenses <CaretRight size={13} weight="bold" className="ml-1.5" />
                </Button>
              </Link>
            )}
            {isAdmin && (
              <Link to="/assessor/marketplace">
                <Button variant="outline" className="h-10 border-white/30 text-white hover:bg-white/10 text-sm font-semibold px-5 rounded" data-testid="mp-to-admin">
                  Marketplace Admin
                </Button>
              </Link>
            )}
            <Link to="/portfolio">
              <Button variant="outline" className="h-10 border-white/30 text-white hover:bg-white/10 text-sm font-semibold px-5 rounded" data-testid="mp-to-portfolio">
                Enterprise quote →
              </Button>
            </Link>
          </div>
          <PillarStrip />
        </div>
      </section>

      {/* Catalog */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-10">
        {/* Family tabs */}
        <div className="flex flex-wrap gap-1.5 mb-6 border-b border-gray-200">
          {catalog?.families?.map(f => {
            const Icon = FAMILY_ICON[f.key] || Database;
            const active = activeTab === f.key;
            return (
              <button
                key={f.key} onClick={() => setActiveTab(f.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-t-md border-b-2 text-sm font-semibold transition-colors ${
                  active ? 'border-[#FF4E6A] text-[#111827]' : 'border-transparent text-[#4B5563] hover:text-[#111827]'
                }`}
                data-testid={`mp-tab-${f.key}`}>
                <Icon size={15} weight="duotone" />
                {f.name}
              </button>
            );
          })}
        </div>

        {/* Active family content */}
        {activeFamily && (
          <div>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded bg-[#0B1426] flex items-center justify-center flex-shrink-0">
                <ActiveIcon size={20} weight="duotone" className="text-[#FF4E6A]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#111827]">{activeFamily.name}</h2>
                <p className="text-sm text-[#4B5563]">{activeFamily.tagline}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`mp-tier-grid-${activeFamily.key}`}>
              {activeFamily.tiers.map(t => (
                <TierCard key={t.code} family={activeFamily} tier={t}
                  isAuthenticated={isAuthenticated}
                  onSubscribe={onSubscribe} onPurchase={onPurchase} onQuote={onQuote} />
              ))}
            </div>
          </div>
        )}

        {/* Security strip */}
        {catalog?.disclaimer && (
          <div className="mt-10 p-5 rounded-lg bg-[#F3F4F6] border-l-4 border-[#0B1426]" data-testid="mp-security-strip">
            <div className="flex items-start gap-3">
              <Lock size={18} weight="duotone" className="text-[#0B1426] mt-0.5" />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0B1426] mb-1">Architectural protection</p>
                <p className="text-sm text-[#374151]">{catalog.disclaimer}</p>
              </div>
            </div>
          </div>
        )}

        {/* Light positioning strip */}
        <div className="mt-10 p-6 rounded-lg bg-[#0B1426] text-white grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <Sparkle size={18} weight="duotone" className="text-[#FF4E6A]" />
            <h3 className="text-base font-bold mt-2">Designed as scientific infrastructure — not consumer scoring.</h3>
            <p className="text-[12px] text-white/60 mt-1">Used by retailer compliance teams, MoCRA-prep brands, ingestible launches, and packaging-system owners.</p>
          </div>
          <div className="text-[11px] text-white/70">
            <p className="font-bold text-white/90 mb-1">For brand teams</p>
            Self-serve subscriptions and on-demand reports for retailer-readiness, regulatory triage, and contaminant surveillance.
          </div>
          <div className="text-[11px] text-white/70">
            <p className="font-bold text-white/90 mb-1">For enterprise platforms</p>
            Tokenised API licensing for ERP/PLM, retailer programmes, and white-label compliance workflows — quote on request.
          </div>
        </div>
      </main>

      <SiteFooter />

      <SubscribeDialog open={subOpen} onOpenChange={setSubOpen} family={subFamily} tier={subTier} onSubmitted={() => { navigate('/marketplace/my-licenses'); }} />
      <PurchaseDialog open={purOpen} onOpenChange={setPurOpen} family={purFamily} tier={purTier} onSubmitted={() => { navigate('/marketplace/my-licenses'); }} />
    </div>
  );
}
